import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import AnonymousWelcome from '../components/Crew/AnonymousWelcome';
import SessionErrorDisplay from '../components/Crew/SessionErrorDisplay';
import DualCrewView from '../components/DualCrew/DualCrewView';
import {
  saveParticipantSession,
  loadParticipantSession,
  cleanupExpiredParticipantSessions,
  removeParticipantSession
} from '../general-utilities/participantSessionStorage';
import { CrewService } from '../services/CrewService';
import { ValidationException } from '../services/Exceptions';
import sessionStore from '../stores/SessionStore';

export type SessionStatus = 'loading' | 'enabled' | 'disabled' | 'error' | 'ended';
const SESSION_DISABLED = 'SESSION_DISABLED';

const DualCrew: React.FC = observer(() => {
  const { sessionId, droneUuid } = useParams<{ sessionId?: string; droneUuid?: string }>();
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<SessionStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { isAuthenticated } = sessionStore;

  useEffect(() => {
    cleanupExpiredParticipantSessions();

    if (!sessionId) return;

    setErrorStatus('loading');
    setErrorMessage(null);

    if (isAuthenticated) {
      CrewService.joinSharedSession(sessionId)
        .then((response) => {
          if (response && response.id) {
            setParticipantId(response.id);
            setErrorStatus('enabled');
            setErrorMessage(null);
          }
        })
        .catch((error) => {
          if (error instanceof ValidationException) {
            const errorMsg = error.message || '';
            if (errorMsg === SESSION_DISABLED) {
              setErrorStatus('disabled');
              setErrorMessage('disabled');
            } else {
              setErrorStatus('error');
              setErrorMessage('server_error');
            }
          } else {
            setErrorStatus('error');
            setErrorMessage('server_error');
          }
          setParticipantId(null);
        });
      return;
    }

    // Anonymous: check for stored session
    const storedSession = loadParticipantSession(sessionId);
    if (storedSession) {
      setParticipantId(storedSession.participantId);
      setErrorStatus('enabled');
      setErrorMessage(null);
    }
  }, [sessionId, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && sessionId) {
      removeParticipantSession(sessionId);
    }
  }, [isAuthenticated, sessionId]);

  useEffect(() => {
    return () => {
      cleanupExpiredParticipantSessions();
    };
  }, []);

  const handleAnonymousJoin = (name: string) => {
    if (!sessionId) return;

    setErrorStatus('loading');
    setErrorMessage(null);

    CrewService.joinSharedSessionAnonymous(sessionId, name)
      .then((response) => {
        if (response && response.id) {
          const newParticipantId = response.id;
          setParticipantId(newParticipantId);
          setErrorStatus('enabled');
          setErrorMessage(null);

          if (!isAuthenticated) {
            saveParticipantSession(newParticipantId, sessionId, droneUuid ?? '', name);
          }
        }
      })
      .catch((error) => {
        if (error instanceof ValidationException) {
          const errorMsg = error.message || '';
          if (errorMsg === SESSION_DISABLED) {
            setErrorStatus('disabled');
            setErrorMessage('disabled');
          } else {
            setErrorStatus('error');
            setErrorMessage('server_error');
          }
        } else {
          setErrorStatus('error');
          setErrorMessage('server_error');
        }
        setParticipantId(null);
      });
  };

  if (errorStatus !== 'loading' && errorStatus !== 'enabled' && !participantId) {
    return (
      <SessionErrorDisplay
        status={errorStatus === 'ended' ? 'disabled' : errorStatus}
        error={errorMessage}
        isAuthenticated={isAuthenticated}
      />
    );
  }

  return (
    <>
      {!isAuthenticated && !participantId && <AnonymousWelcome onJoin={handleAnonymousJoin} />}
      {participantId && sessionId && (
        <DualCrewView
          participantId={participantId}
          sessionId={sessionId}
          deviceUuid={droneUuid ?? ''}
          onSessionEnded={() => {
            setErrorStatus('ended');
            setErrorMessage('disabled');
            setParticipantId(null);
          }}
        />
      )}
    </>
  );
});

export default DualCrew;
